import type { SponsorTypeEnum } from "./types";

/**
 * Paid-content disclosure on news (`CommunicationPost`) and module posts
 * (`Post`) — docs/synced/portal-api/05-types.md.
 *
 * The contract: `sponsorType` is `normal`, `sponsored`, `partner` or
 * `affiliate` (2026-10-20, labelled as sponsored); anything but `normal`
 * renders a chip AND a banner, and `sponsorDisclosure` is shown
 * VERBATIM — its wording is set by a regulator, not by the theme, and the
 * backend already falls back across locales so a sponsored item always has one.
 * So this helper decides only WHETHER an item is paid content and which label
 * it carries; it never rewords, trims or translates the disclosure.
 *
 * Fail direction: an unrecognised non-normal value still reads as sponsored.
 * Calling paid content editorial is the expensive mistake; the enum mirror in
 * types.ts and `check:gql` make a new value a gate failure long before that.
 */
export type Sponsorship = {
  kind: "sponsored" | "partner";
  /** Who paid for it; null when the author left it blank. */
  name: string | null;
  /** The regulator-set wording, verbatim. Undefined on card selections, which
   *  do not fetch it; null when the backend has none. */
  disclosure: string | null | undefined;
};

type SponsorFields = {
  sponsorType?: SponsorTypeEnum | null;
  sponsorName?: string | null;
  sponsorDisclosure?: string | null;
};

export function sponsorship(item: SponsorFields | null | undefined): Sponsorship | null {
  const type = item?.sponsorType;
  if (!type || type === "normal") return null;
  const name = item.sponsorName?.trim() || null;
  return {
    kind: type === "partner" ? "partner" : "sponsored",
    name,
    disclosure: item.sponsorDisclosure,
  };
}
